import * as stylex from "@stylexjs/stylex";
import { useForm } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useCookieAuth } from "@animus/frontend-features/auth-provider-cookie";

import {
  alert,
  button,
  card,
  form,
  layout,
  page,
  spacing,
  text,
} from "../../styles.stylex";

export function SignupPage() {
  const navigate = useNavigate();
  const { signUp, isLoading, error } = useCookieAuth();
  const [isPending, setIsPending] = useState(false);

  const formState = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: async ({ value }) => {
      setIsPending(true);
      try {
        await signUp(value.email, value.password, value.name);
        navigate({ to: "/" });
      } finally {
        setIsPending(false);
      }
    },
  });

  return (
    <div {...stylex.props(layout.centered, page.dark)}>
      <div {...stylex.props(card.base)}>
        <h1
          {...stylex.props(
            spacing.mb6,
            text.center,
            text.bold,
            text["2xl"],
            text.white
          )}
        >
          Create Account
        </h1>

        {error && <div {...stylex.props(alert.error)}>{error.message}</div>}

        <form
          {...stylex.props(form.space)}
          onSubmit={(e) => {
            e.preventDefault();
            formState.handleSubmit();
          }}
        >
          <formState.Field name="name">
            {(field) => (
              <div>
                <label {...stylex.props(form.label)} htmlFor={field.name}>
                  Name (optional)
                </label>
                <input
                  {...stylex.props(form.input)}
                  autoComplete="name"
                  id={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="text"
                  value={field.state.value}
                />
              </div>
            )}
          </formState.Field>

          <formState.Field
            name="email"
            validators={{
              onChange: ({ value }) => {
                if (value) {
                  // biome-ignore lint/performance/useTopLevelRegex: TODO: better validation
                  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return;
                  }

                  return "Invalid email address";
                }

                return "Email is required";
              },
            }}
          >
            {(field) => (
              <div>
                <label {...stylex.props(form.label)} htmlFor={field.name}>
                  Email
                </label>
                <input
                  {...stylex.props(form.input)}
                  autoComplete="email"
                  id={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="email"
                  value={field.state.value}
                />
                {field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0 && (
                    <p {...stylex.props(form.error)}>
                      {field.state.meta.errors.join(", ")}
                    </p>
                  )}
              </div>
            )}
          </formState.Field>

          <formState.Field
            name="password"
            validators={{
              onChange: ({ value }) => {
                if (value) {
                  if (value.length < 8) {
                    return "Password must be at least 8 characters";
                  }

                  return;
                }

                return "Password is required";
              },
            }}
          >
            {(field) => (
              <div>
                <label {...stylex.props(form.label)} htmlFor={field.name}>
                  Password
                </label>
                <input
                  {...stylex.props(form.input)}
                  autoComplete="new-password"
                  id={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="password"
                  value={field.state.value}
                />
                {field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0 && (
                    <p {...stylex.props(form.error)}>
                      {field.state.meta.errors.join(", ")}
                    </p>
                  )}
              </div>
            )}
          </formState.Field>

          <formState.Field
            name="confirmPassword"
            validators={{
              onChangeListenTo: ["password"],
              onChange: ({ value, fieldApi }) => {
                const password = fieldApi.form.getFieldValue("password");
                if (!value) {
                  return "Please confirm your password";
                }
                if (value !== password) {
                  return "Passwords do not match";
                }
                return;
              },
            }}
          >
            {(field) => (
              <div>
                <label {...stylex.props(form.label)} htmlFor={field.name}>
                  Confirm Password
                </label>
                <input
                  {...stylex.props(form.input)}
                  autoComplete="new-password"
                  id={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  type="password"
                  value={field.state.value}
                />
                {field.state.meta.isTouched &&
                  field.state.meta.errors.length > 0 && (
                    <p {...stylex.props(form.error)}>
                      {field.state.meta.errors.join(", ")}
                    </p>
                  )}
              </div>
            )}
          </formState.Field>

          <formState.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <button
                {...stylex.props(button.base, button.primary, button.full)}
                disabled={isSubmitting || isPending || isLoading}
                type="submit"
              >
                {isSubmitting || isPending
                  ? "Creating account..."
                  : "Create Account"}
              </button>
            )}
          </formState.Subscribe>
        </form>

        <p {...stylex.props(spacing.mt4, text.center, text.sm, text.muted)}>
          Already have an account?{" "}
          <Link {...stylex.props(button.link)} to="/login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
